/**
 * Amazon Expenses Tab Component
 * Handles import from Amazon Analyzer and manual entry of Amazon expenses
 * Structure: Country -> Fulfillment (FBA, FBM, FBM-US) -> Category
 */

import React, { useState, useRef, useMemo } from 'react';
import {
  Upload,

  Plus,
  Trash2,
  ChevronDown,
  ChevronUp,
  X,
  AlertCircle,
  CheckCircle,
  FileJson,
  Copy,
  RefreshCw,
} from 'lucide-react';
import type { CustomMarketplace } from '../../types/database';
import { useModalA11y } from '../../hooks/useModalA11y';

// Types for Amazon Expenses V2 format
interface CategoryExpenseEntry {
  sellingFeePercent: number;
  fbaFeePercent: number;
  advertisingPercent: number;
  refundLossPercent: number;
  marginPercent: number;
  roiPercent: number;
  sampleSize: number;
  totalRevenue: number;
  totalCost: number;
  totalProfit: number;
}

interface FulfillmentExpenses {
  totalRevenue: number;
  totalOrders: number;
  categories: {
    [category: string]: CategoryExpenseEntry;
  };
}

interface AmazonExpensesExportV2 {
  version: 2;
  exportedAt: string;
  sourceApp: 'amazon-analyzer';
  period: {
    start: string;
    end: string;
  };
  marketplaces: {
    [marketplaceCode: string]: {
      FBA?: FulfillmentExpenses;
      FBM?: FulfillmentExpenses;
      'FBM-US'?: FulfillmentExpenses;
    };
  };
  summary: {
    totalMarketplaces: number;
    totalCategories: number;
    totalRevenue: number;
    totalOrders: number;
  };
}

// Local storage structure for expenses
export interface StoredExpense {
  id: string;
  countryCode: string;
  fulfillmentType: 'FBA' | 'FBM' | 'FBM-TR' | 'FBM-US';
  category: string;
  sellingFeePercent: number;
  fbaFeePercent: number;
  advertisingPercent: number;
  refundLossPercent: number;
  fbaCostPercent?: number;
  fbmCostPercent?: number;
  vatPercent?: number;
  gstPercent?: number;
  customsPercent?: number;
  ddpPercent?: number;
  shipPercent?: number;
  warehousePercent?: number;
  marginPercent: number;
  roiPercent: number;
  sampleSize: number;
  dataSource: 'imported' | 'manual';
  periodStart?: string;
  periodEnd?: string;
  updatedAt: Date;
}

interface AmazonExpensesTabProps {
  selectedMarketplace: CustomMarketplace;
  expenses: StoredExpense[];
  setExpenses: (expenses: StoredExpense[] | ((prev: StoredExpense[]) => StoredExpense[])) => void;
  categories: string[];
}

// Country flags
const FLAGS: Record<string, string> = {
  US: '🇺🇸', UK: '🇬🇧', DE: '🇩🇪', FR: '🇫🇷', IT: '🇮🇹', ES: '🇪🇸',
  CA: '🇨🇦', AU: '🇦🇺', AE: '🇦🇪', SA: '🇸🇦', SG: '🇸🇬', JP: '🇯🇵',
  MX: '🇲🇽', BR: '🇧🇷', IN: '🇮🇳',
};

const AmazonExpensesTab: React.FC<AmazonExpensesTabProps> = ({
  selectedMarketplace,
  expenses,
  setExpenses,
  categories,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isImporting, setIsImporting] = useState(false);
  const [importResult, setImportResult] = useState<{ success: number; updated: number } | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Filter state
  const [filterCountry, setFilterCountry] = useState<string>('');
  const [filterFulfillment, setFilterFulfillment] = useState<string>('');
  const [filterCategory, setFilterCategory] = useState<string>('');
  const [expandedId, setExpandedId] = useState<string | null>(null);

  // Add/Edit modal state
  const [isAddingExpense, setIsAddingExpense] = useState(false);
  const [editingExpense, setEditingExpense] = useState<StoredExpense | null>(null);
  const [newExpense, setNewExpense] = useState<Partial<StoredExpense>>({
    countryCode: '',
    fulfillmentType: 'FBA',
    category: '',
    sellingFeePercent: 15,
    fbaFeePercent: 0,
    advertisingPercent: 5,
    refundLossPercent: 2,
    marginPercent: 20,
    roiPercent: 0,
  });

  // Modal a11y (Escape + initial focus)
  const addExpenseModalRef = useModalA11y(isAddingExpense, () => {
    setIsAddingExpense(false);
    setEditingExpense(null);
  });

  // Get unique values for filters
  const uniqueCountries = useMemo(() => {
    const codes = new Set(expenses.map(e => e.countryCode));
    return Array.from(codes).sort();
  }, [expenses]);

  const uniqueCategories = useMemo(() => {
    const cats = new Set(expenses.map(e => e.category));
    return Array.from(cats).sort();
  }, [expenses]);

  // Filter expenses
  const filteredExpenses = useMemo(() => {
    return expenses.filter(e =>
      (!filterCountry || e.countryCode === filterCountry) &&
      (!filterFulfillment || e.fulfillmentType === filterFulfillment) &&
      (!filterCategory || e.category === filterCategory)
    );
  }, [expenses, filterCountry, filterFulfillment, filterCategory]);

  // Group by category for summary view
  const groupedByCategory = useMemo(() => {
    if (filterCountry || filterFulfillment || filterCategory) return null;

    const groups: Record<string, StoredExpense[]> = {};
    expenses.forEach(e => {
      if (!groups[e.category]) groups[e.category] = [];
      groups[e.category].push(e);
    });
    return groups;
  }, [expenses, filterCountry, filterFulfillment, filterCategory]);

  // Detect if JSON is a bulk pricing export (vs Amazon Expenses V2)
  const isPricingExport = (data: Record<string, unknown>): boolean => {
    return data.version === 2 &&
      data.sourceApp === 'amazon-analyzer' &&
      'dateRange' in data &&
      !('period' in data);
  };

  // Import bulk pricing export format (from AmzSellMetrics "PriceLab Export")
  const importPricingExport = (data: Record<string, unknown>): { imported: number; updated: number; newExpenses: StoredExpense[] } => {
    const dateRange = data.dateRange as { start: string; end: string };
    const marketplaces = data.marketplaces as Record<string, {
      categories: Array<{
        category: string;
        marketplace: string;
        fulfillmentType: 'FBA' | 'FBM' | 'FBM-TR' | 'FBM-US';
        sellingFeePercent: number;
        fbaFeePercent: number;
        refundLossPercent: number;
        vatPercent: number;
        productCostPercent: number;
        gstCostPercent: number;
        periodStart: string;
        periodEnd: string;
      }>;
      globalSettings: { advertisingPercent: number; fbaCostPercent: number; fbmCostPercent: number };
    }>;

    let imported = 0;
    let updated = 0;
    // Start fresh - replace all existing expenses
    const newExpenses: StoredExpense[] = [];

    Object.entries(marketplaces).forEach(([countryCode, mpData]) => {
      if (!mpData.categories) return;
      const adsPercent = mpData.globalSettings?.advertisingPercent || 0;

      const fbaCost = mpData.globalSettings?.fbaCostPercent || 0;
      const fbmCost = mpData.globalSettings?.fbmCostPercent || 0;

      mpData.categories.forEach(cat => {
        const ft = cat.fulfillmentType as 'FBA' | 'FBM' | 'FBM-TR' | 'FBM-US';

        newExpenses.push({
          id: `${countryCode}-${ft}-${cat.category}-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
          countryCode,
          fulfillmentType: ft,
          category: cat.category,
          sellingFeePercent: cat.sellingFeePercent,
          fbaFeePercent: cat.fbaFeePercent,
          advertisingPercent: adsPercent,
          refundLossPercent: cat.refundLossPercent,
          fbaCostPercent: fbaCost,
          fbmCostPercent: fbmCost,
          vatPercent: cat.vatPercent || 0,
          gstPercent: cat.gstCostPercent || 0,
          marginPercent: 0,
          roiPercent: 0,
          sampleSize: 0,
          dataSource: 'imported',
          periodStart: cat.periodStart || dateRange.start,
          periodEnd: cat.periodEnd || dateRange.end,
          updatedAt: new Date(),
        });
        imported++;
      });
    });

    return { imported, updated, newExpenses };
  };

  // Import Amazon Expenses V2 format
  const importExpensesV2 = (data: AmazonExpensesExportV2): { imported: number; updated: number; newExpenses: StoredExpense[] } => {
    let imported = 0;
    let updated = 0;
    const newExpenses: StoredExpense[] = [...expenses];

    Object.entries(data.marketplaces).forEach(([countryCode, fulfillments]) => {
      (['FBA', 'FBM', 'FBM-US'] as const).forEach(fulfillmentType => {
        const fulfillmentData = fulfillments[fulfillmentType];
        if (!fulfillmentData?.categories) return;

        Object.entries(fulfillmentData.categories).forEach(([category, catData]) => {
          const existingIndex = newExpenses.findIndex(
            e => e.countryCode === countryCode &&
                 e.fulfillmentType === fulfillmentType &&
                 e.category === category
          );

          const expense: StoredExpense = {
            id: existingIndex >= 0 ? newExpenses[existingIndex].id : `${countryCode}-${fulfillmentType}-${category}-${Date.now()}`,
            countryCode,
            fulfillmentType,
            category,
            sellingFeePercent: catData.sellingFeePercent,
            fbaFeePercent: catData.fbaFeePercent,
            advertisingPercent: catData.advertisingPercent,
            refundLossPercent: catData.refundLossPercent,
            marginPercent: catData.marginPercent,
            roiPercent: catData.roiPercent,
            sampleSize: catData.sampleSize,
            dataSource: 'imported',
            periodStart: data.period.start,
            periodEnd: data.period.end,
            updatedAt: new Date(),
          };

          if (existingIndex >= 0) {
            newExpenses[existingIndex] = expense;
            updated++;
          } else {
            newExpenses.push(expense);
            imported++;
          }
        });
      });
    });

    return { imported, updated, newExpenses };
  };

  // Handle JSON import (supports both formats)
  const handleImport = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setIsImporting(true);
    setError(null);
    setImportResult(null);

    try {
      const text = await file.text();
      const data = JSON.parse(text);

      if (!data.version || data.version !== 2 || !data.marketplaces) {
        throw new Error('Geçersiz dosya formatı. Amazon Analyzer V2 formatı gerekli.');
      }

      let result: { imported: number; updated: number; newExpenses: StoredExpense[] };

      if (isPricingExport(data)) {
        result = importPricingExport(data);
      } else if (data.period) {
        result = importExpensesV2(data as AmazonExpensesExportV2);
      } else {
        throw new Error('Tanınmayan dosya formatı.');
      }

      setExpenses(result.newExpenses);
      setImportResult({ success: result.imported, updated: result.updated });
      setTimeout(() => setImportResult(null), 5000);
    } catch (err: unknown) {
      setError((err instanceof Error ? err.message : String(err)) || 'Import ba\u015Far\u0131s\u0131z');
    } finally {
      setIsImporting(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Handle manual add/edit
  const handleSaveExpense = () => {
    if (!newExpense.countryCode || !newExpense.category || !newExpense.fulfillmentType) {
      setError('Ülke, fulfillment ve kategori seçilmeli');
      return;
    }

    const expense: StoredExpense = {
      id: editingExpense?.id || `${newExpense.countryCode}-${newExpense.fulfillmentType}-${newExpense.category}-${Date.now()}`,
      countryCode: newExpense.countryCode!,
      fulfillmentType: newExpense.fulfillmentType as 'FBA' | 'FBM' | 'FBM-TR' | 'FBM-US',
      category: newExpense.category!,
      sellingFeePercent: newExpense.sellingFeePercent || 0,
      fbaFeePercent: newExpense.fbaFeePercent || 0,
      advertisingPercent: newExpense.advertisingPercent || 0,
      refundLossPercent: newExpense.refundLossPercent || 0,
      marginPercent: newExpense.marginPercent || 0,
      roiPercent: newExpense.roiPercent || 0,
      sampleSize: newExpense.sampleSize || 0,
      dataSource: 'manual',
      updatedAt: new Date(),
    };

    if (editingExpense) {
      setExpenses(prev => prev.map(e => e.id === editingExpense.id ? expense : e));
    } else {
      // Check if already exists
      const exists = expenses.some(
        e => e.countryCode === expense.countryCode &&
             e.fulfillmentType === expense.fulfillmentType &&
             e.category === expense.category
      );
      if (exists) {
        setError('Bu kombinasyon zaten mevcut');
        return;
      }
      setExpenses(prev => [...prev, expense]);
    }

    setIsAddingExpense(false);
    setEditingExpense(null);
    setNewExpense({
      countryCode: '',
      fulfillmentType: 'FBA',
      category: '',
      sellingFeePercent: 15,
      fbaFeePercent: 0,
      advertisingPercent: 5,
      refundLossPercent: 2,
      marginPercent: 20,
      roiPercent: 0,
    });
  };

  // Handle delete
  const handleDelete = (id: string) => {
    if (window.confirm('Bu kaydı silmek istediğinizden emin misiniz?')) {
      setExpenses(prev => prev.filter(e => e.id !== id));
    }
  };

  return (
    <div className="space-y-6">
      {/* Header with Import/Export */}
      <div className="flex items-center justify-between">
        <div>
          <h4 className="text-sm font-medium text-slate-700">Amazon Masrafları</h4>
          <p className="text-xs text-slate-500 mt-1">
            Kategori bazlı Amazon gider oranları (selling fee, FBA fee, ads, refund) ve karlılık metrikleri
          </p>
        </div>
        <div className="flex items-center gap-2">
          <input
            ref={fileInputRef}
            type="file"
            accept=".json"
            onChange={handleImport}
            className="hidden"
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={isImporting}
            className="flex items-center gap-2 px-3 py-2 text-purple-600 bg-purple-50 hover:bg-purple-100 rounded-lg font-medium transition-colors"
          >
            {isImporting ? (
              <RefreshCw className="w-4 h-4 animate-spin" />
            ) : (
              <Upload className="w-4 h-4" />
            )}
            JSON Yükle
          </button>
          <button
            onClick={() => {
              setNewExpense({
                countryCode: selectedMarketplace.countries.find(c => c.isActive)?.code || '',
                fulfillmentType: 'FBA',
                category: categories[0] || '',
                sellingFeePercent: 15,
                fbaFeePercent: 0,
                advertisingPercent: 5,
                refundLossPercent: 2,
                marginPercent: 20,
                roiPercent: 0,
              });
              setEditingExpense(null);
              setIsAddingExpense(true);
            }}
            className="flex items-center gap-2 px-3 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-medium transition-colors"
          >
            <Plus className="w-4 h-4" />
            Manuel Ekle
          </button>
        </div>
      </div>

      {/* Messages */}
      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-50 text-red-700 rounded-lg">
          <AlertCircle className="w-4 h-4" />
          <span className="text-sm">{error}</span>
          <button onClick={() => setError(null)} className="ml-auto">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {importResult && (
        <div className="flex items-center gap-2 p-3 bg-green-50 text-green-700 rounded-lg">
          <CheckCircle className="w-4 h-4" />
          <span className="text-sm">
            {importResult.success} yeni kayıt eklendi, {importResult.updated} kayıt güncellendi
          </span>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2 p-3 bg-slate-50 rounded-lg">
        <select
          value={filterCountry}
          onChange={(e) => setFilterCountry(e.target.value)}
          className="px-3 py-1.5 text-sm border border-slate-200 rounded-lg bg-white"
        >
          <option value="">Tüm Ülkeler</option>
          {uniqueCountries.map(code => (
            <option key={code} value={code}>
              {FLAGS[code] || '🏳️'} {code}
            </option>
          ))}
        </select>
        <select
          value={filterFulfillment}
          onChange={(e) => setFilterFulfillment(e.target.value)}
          className="px-3 py-1.5 text-sm border border-slate-200 rounded-lg bg-white"
        >
          <option value="">Tüm Fulfillment</option>
          <option value="FBA">FBA</option>
          <option value="FBM">FBM</option>
          <option value="FBM-TR">FBM-TR</option>
          <option value="FBM-US">FBM-US</option>
        </select>
        <select
          value={filterCategory}
          onChange={(e) => setFilterCategory(e.target.value)}
          className="px-3 py-1.5 text-sm border border-slate-200 rounded-lg bg-white"
        >
          <option value="">Tüm Kategoriler</option>
          {uniqueCategories.map(cat => (
            <option key={cat} value={cat}>{cat}</option>
          ))}
        </select>
        {(filterCountry || filterFulfillment || filterCategory) && (
          <button
            onClick={() => {
              setFilterCountry('');
              setFilterFulfillment('');
              setFilterCategory('');
            }}
            className="px-2 py-1 text-xs text-slate-600 hover:bg-slate-200 rounded"
          >
            Temizle
          </button>
        )}
        <span className="ml-auto text-xs text-slate-500">
          {filteredExpenses.length} kayıt
        </span>
      </div>

      {/* Expense List */}
      {expenses.length === 0 ? (
        <div className="text-center py-12 text-slate-500">
          <FileJson className="w-12 h-12 mx-auto mb-4 text-slate-300" />
          <p className="mb-2">Henüz Amazon masraf kaydı yok</p>
          <p className="text-xs">
            Amazon Analyzer'dan JSON yükleyin veya manuel ekleyin
          </p>
        </div>
      ) : groupedByCategory ? (
        // Summary view grouped by category
        <div className="space-y-2">
          {Object.entries(groupedByCategory)
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([category, items]) => {
              const uniqueCountryCodes = Array.from(new Set(items.map(i => i.countryCode)));
              const hasFBA = items.some(i => i.fulfillmentType === 'FBA');
              const hasFBM = items.some(i => i.fulfillmentType === 'FBM');
              const hasFBMTR = items.some(i => i.fulfillmentType === 'FBM-TR');
              const hasFBMUS = items.some(i => i.fulfillmentType === 'FBM-US');

              return (
                <div
                  key={category}
                  onClick={() => setFilterCategory(category)}
                  className="flex items-center justify-between p-3 bg-slate-50 border border-slate-200 rounded-lg cursor-pointer hover:bg-slate-100 hover:border-indigo-300 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <span className="font-medium text-slate-800">{category === '__default__' ? 'Genel (Varsayılan)' : category}</span>
                    {category === '__default__' && <span className="px-2 py-0.5 text-xs rounded bg-gray-100 text-gray-700">Default</span>}
                    <div className="flex gap-1">
                      {hasFBA && <span className="px-2 py-0.5 text-xs rounded bg-orange-100 text-orange-700">FBA</span>}
                      {hasFBM && <span className="px-2 py-0.5 text-xs rounded bg-blue-100 text-blue-700">FBM</span>}
                      {hasFBMTR && <span className="px-2 py-0.5 text-xs rounded bg-teal-100 text-teal-700">FBM-TR</span>}
                      {hasFBMUS && <span className="px-2 py-0.5 text-xs rounded bg-green-100 text-green-700">FBM-US</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="flex -space-x-1">
                      {uniqueCountryCodes.slice(0, 5).map(code => (
                        <span key={code} className="text-sm" title={code}>
                          {FLAGS[code] || '🏳️'}
                        </span>
                      ))}
                      {uniqueCountryCodes.length > 5 && (
                        <span className="text-xs text-slate-500 ml-1">+{uniqueCountryCodes.length - 5}</span>
                      )}
                    </div>
                    <span className="text-xs text-slate-500">{items.length} kayıt</span>
                    <ChevronDown className="w-4 h-4 text-slate-400" />
                  </div>
                </div>
              );
            })}
        </div>
      ) : (
        // Detailed list view
        <div className="space-y-2">
          {filterCategory && (
            <div className="flex items-center justify-between mb-3 pb-2 border-b border-slate-200">
              <div className="flex items-center gap-2">
                <span className="font-semibold text-slate-800">{filterCategory}</span>
                <span className="text-sm text-slate-500">({filteredExpenses.length} kayıt)</span>
              </div>
              <button
                onClick={() => setFilterCategory('')}
                className="text-xs text-indigo-600 hover:text-indigo-800"
              >
                ← Tüm Kategoriler
              </button>
            </div>
          )}

          {filteredExpenses
            .sort((a, b) => `${a.countryCode}-${a.fulfillmentType}-${a.category}`.localeCompare(`${b.countryCode}-${b.fulfillmentType}-${b.category}`))
            .map(expense => {
              const isExpanded = expandedId === expense.id;

              return (
                <div key={expense.id} className="border border-slate-200 rounded-lg overflow-hidden">
                  {/* Header Row */}
                  <div
                    className="flex items-center justify-between p-3 bg-slate-50 cursor-pointer hover:bg-slate-100"
                    onClick={() => setExpandedId(isExpanded ? null : expense.id)}
                  >
                    <div className="flex items-center gap-3">
                      {isExpanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                      <span className="text-lg">{FLAGS[expense.countryCode] || '🏳️'}</span>
                      <span className="font-medium text-slate-800">{expense.category === '__default__' ? 'Genel (Varsayılan)' : expense.category}</span>
                      {expense.category === '__default__' && <span className="px-2 py-0.5 text-xs rounded bg-gray-100 text-gray-700">Default</span>}
                      <span className={`px-2 py-0.5 text-xs rounded ${
                        expense.fulfillmentType === 'FBA' ? 'bg-orange-100 text-orange-700' :
                        expense.fulfillmentType === 'FBM-TR' ? 'bg-teal-100 text-teal-700' :
                        expense.fulfillmentType === 'FBM-US' ? 'bg-green-100 text-green-700' :
                        'bg-blue-100 text-blue-700'
                      }`}>
                        {expense.fulfillmentType}
                      </span>
                      {expense.dataSource === 'imported' && (
                        <span className="px-2 py-0.5 text-xs rounded bg-purple-100 text-purple-700">
                          Imported
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="text-xs text-slate-500">
                        Marj: <span className="font-medium text-green-600">{expense.marginPercent.toFixed(1)}%</span>
                      </span>
                      <span className="text-xs text-slate-500">
                        ROI: <span className="font-medium text-blue-600">{expense.roiPercent.toFixed(1)}%</span>
                      </span>
                      <span className="text-xs text-slate-400 hidden md:inline">
                        Sell: {expense.sellingFeePercent.toFixed(1)}% |
                        FBA: {expense.fbaFeePercent.toFixed(1)}% |
                        Ads: {expense.advertisingPercent.toFixed(1)}%
                        {(expense.fbaCostPercent || expense.fbmCostPercent) ? ` | FBA Cost: ${(expense.fbaCostPercent || 0).toFixed(1)}% | FBM Cost: ${(expense.fbmCostPercent || 0).toFixed(1)}%` : ''}
                      </span>
                    </div>
                  </div>

                  {/* Expanded Content */}
                  {isExpanded && (
                    <div className="p-4 border-t border-slate-200 bg-white">
                      <div className="grid grid-cols-4 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-slate-600 mb-1">Selling Fee %</label>
                          <input
                            type="number"
                            value={Math.round(expense.sellingFeePercent * 100) / 100}
                            onChange={(e) => {
                              const value = parseFloat(e.target.value) || 0;
                              setExpenses(prev => prev.map(ex =>
                                ex.id === expense.id ? { ...ex, sellingFeePercent: value, dataSource: 'manual', updatedAt: new Date() } : ex
                              ));
                            }}
                            className="w-full px-2 py-1.5 text-sm border border-slate-200 rounded-lg"
                            step="0.1"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-slate-600 mb-1">FBA Fee %</label>
                          <input
                            type="number"
                            value={Math.round(expense.fbaFeePercent * 100) / 100}
                            onChange={(e) => {
                              const value = parseFloat(e.target.value) || 0;
                              setExpenses(prev => prev.map(ex =>
                                ex.id === expense.id ? { ...ex, fbaFeePercent: value, dataSource: 'manual', updatedAt: new Date() } : ex
                              ));
                            }}
                            className="w-full px-2 py-1.5 text-sm border border-slate-200 rounded-lg"
                            step="0.1"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-slate-600 mb-1">Ads %</label>
                          <input
                            type="number"
                            value={Math.round(expense.advertisingPercent * 100) / 100}
                            onChange={(e) => {
                              const value = parseFloat(e.target.value) || 0;
                              setExpenses(prev => prev.map(ex =>
                                ex.id === expense.id ? { ...ex, advertisingPercent: value, dataSource: 'manual', updatedAt: new Date() } : ex
                              ));
                            }}
                            className="w-full px-2 py-1.5 text-sm border border-slate-200 rounded-lg"
                            step="0.1"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-slate-600 mb-1">Refund %</label>
                          <input
                            type="number"
                            value={Math.round(expense.refundLossPercent * 100) / 100}
                            onChange={(e) => {
                              const value = parseFloat(e.target.value) || 0;
                              setExpenses(prev => prev.map(ex =>
                                ex.id === expense.id ? { ...ex, refundLossPercent: value, dataSource: 'manual', updatedAt: new Date() } : ex
                              ));
                            }}
                            className="w-full px-2 py-1.5 text-sm border border-slate-200 rounded-lg"
                            step="0.1"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-slate-600 mb-1">FBA Cost %</label>
                          <input
                            type="number"
                            value={Math.round((expense.fbaCostPercent || 0) * 100) / 100}
                            onChange={(e) => {
                              const value = parseFloat(e.target.value) || 0;
                              setExpenses(prev => prev.map(ex =>
                                ex.id === expense.id ? { ...ex, fbaCostPercent: value, dataSource: 'manual', updatedAt: new Date() } : ex
                              ));
                            }}
                            className="w-full px-2 py-1.5 text-sm border border-slate-200 rounded-lg"
                            step="0.1"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-slate-600 mb-1">FBM Cost %</label>
                          <input
                            type="number"
                            value={Math.round((expense.fbmCostPercent || 0) * 100) / 100}
                            onChange={(e) => {
                              const value = parseFloat(e.target.value) || 0;
                              setExpenses(prev => prev.map(ex =>
                                ex.id === expense.id ? { ...ex, fbmCostPercent: value, dataSource: 'manual', updatedAt: new Date() } : ex
                              ));
                            }}
                            className="w-full px-2 py-1.5 text-sm border border-slate-200 rounded-lg"
                            step="0.1"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-slate-600 mb-1">Marj %</label>
                          <input
                            type="number"
                            value={Math.round(expense.marginPercent * 100) / 100}
                            onChange={(e) => {
                              const value = parseFloat(e.target.value) || 0;
                              setExpenses(prev => prev.map(ex =>
                                ex.id === expense.id ? { ...ex, marginPercent: value, dataSource: 'manual', updatedAt: new Date() } : ex
                              ));
                            }}
                            className="w-full px-2 py-1.5 text-sm border border-slate-200 rounded-lg"
                            step="0.1"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-slate-600 mb-1">ROI %</label>
                          <input
                            type="number"
                            value={Math.round(expense.roiPercent * 100) / 100}
                            onChange={(e) => {
                              const value = parseFloat(e.target.value) || 0;
                              setExpenses(prev => prev.map(ex =>
                                ex.id === expense.id ? { ...ex, roiPercent: value, dataSource: 'manual', updatedAt: new Date() } : ex
                              ));
                            }}
                            className="w-full px-2 py-1.5 text-sm border border-slate-200 rounded-lg"
                            step="0.1"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-slate-600 mb-1">VAT %</label>
                          <input
                            type="number"
                            value={Math.round((expense.vatPercent || 0) * 100) / 100}
                            onChange={(e) => {
                              const value = parseFloat(e.target.value) || 0;
                              setExpenses(prev => prev.map(ex =>
                                ex.id === expense.id ? { ...ex, vatPercent: value, dataSource: 'manual', updatedAt: new Date() } : ex
                              ));
                            }}
                            className="w-full px-2 py-1.5 text-sm border border-slate-200 rounded-lg"
                            step="0.1"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-slate-600 mb-1">GST %</label>
                          <input
                            type="number"
                            value={Math.round((expense.gstPercent || 0) * 100) / 100}
                            onChange={(e) => {
                              const value = parseFloat(e.target.value) || 0;
                              setExpenses(prev => prev.map(ex =>
                                ex.id === expense.id ? { ...ex, gstPercent: value, dataSource: 'manual', updatedAt: new Date() } : ex
                              ));
                            }}
                            className="w-full px-2 py-1.5 text-sm border border-slate-200 rounded-lg"
                            step="0.1"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-slate-600 mb-1">Customs %</label>
                          <input
                            type="number"
                            value={Math.round((expense.customsPercent || 0) * 100) / 100}
                            onChange={(e) => {
                              const value = parseFloat(e.target.value) || 0;
                              setExpenses(prev => prev.map(ex =>
                                ex.id === expense.id ? { ...ex, customsPercent: value, dataSource: 'manual', updatedAt: new Date() } : ex
                              ));
                            }}
                            className="w-full px-2 py-1.5 text-sm border border-slate-200 rounded-lg"
                            step="0.1"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-slate-600 mb-1">DDP %</label>
                          <input
                            type="number"
                            value={Math.round((expense.ddpPercent || 0) * 100) / 100}
                            onChange={(e) => {
                              const value = parseFloat(e.target.value) || 0;
                              setExpenses(prev => prev.map(ex =>
                                ex.id === expense.id ? { ...ex, ddpPercent: value, dataSource: 'manual', updatedAt: new Date() } : ex
                              ));
                            }}
                            className="w-full px-2 py-1.5 text-sm border border-slate-200 rounded-lg"
                            step="0.1"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-slate-600 mb-1">Ship %</label>
                          <input
                            type="number"
                            value={Math.round((expense.shipPercent || 0) * 100) / 100}
                            onChange={(e) => {
                              const value = parseFloat(e.target.value) || 0;
                              setExpenses(prev => prev.map(ex =>
                                ex.id === expense.id ? { ...ex, shipPercent: value, dataSource: 'manual', updatedAt: new Date() } : ex
                              ));
                            }}
                            className="w-full px-2 py-1.5 text-sm border border-slate-200 rounded-lg"
                            step="0.1"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-slate-600 mb-1">Warehouse %</label>
                          <input
                            type="number"
                            value={Math.round((expense.warehousePercent || 0) * 100) / 100}
                            onChange={(e) => {
                              const value = parseFloat(e.target.value) || 0;
                              setExpenses(prev => prev.map(ex =>
                                ex.id === expense.id ? { ...ex, warehousePercent: value, dataSource: 'manual', updatedAt: new Date() } : ex
                              ));
                            }}
                            className="w-full px-2 py-1.5 text-sm border border-slate-200 rounded-lg"
                            step="0.1"
                          />
                        </div>
                      </div>

                      <div className="mt-3 flex items-center justify-between">
                        <div className="text-xs text-slate-400 flex flex-wrap gap-4">
                          <span>Güncelleme: {expense.updatedAt.toLocaleString('tr-TR')}</span>
                          {expense.dataSource === 'imported' && expense.periodStart && expense.periodEnd && (
                            <span className="text-purple-600">
                              Dönem: {expense.periodStart} - {expense.periodEnd}
                            </span>
                          )}
                          {expense.sampleSize > 0 && (
                            <span className="text-purple-600">Örnek: {expense.sampleSize} sipariş</span>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setNewExpense({ ...expense });
                              setEditingExpense(expense);
                              setIsAddingExpense(true);
                            }}
                            className="p-1.5 text-slate-500 hover:bg-slate-100 rounded"
                            title="Düzenle"
                          >
                            <Copy className="w-4 h-4" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(expense.id);
                            }}
                            className="p-1.5 text-red-500 hover:bg-red-50 rounded"
                            title="Sil"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
        </div>
      )}

      {/* Add/Edit Modal */}
      {isAddingExpense && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div
            ref={addExpenseModalRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="amazon-expense-modal-title"
            className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <h3 id="amazon-expense-modal-title" className="font-semibold text-slate-800">
                {editingExpense ? 'Masraf Düzenle' : 'Yeni Amazon Masrafı'}
              </h3>
              <button
                onClick={() => {
                  setIsAddingExpense(false);
                  setEditingExpense(null);
                }}
                className="p-1 hover:bg-slate-100 rounded"
                aria-label="Kapat"
              >
                <X className="w-5 h-5 text-slate-500" aria-hidden="true" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              {/* Selection Row */}
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Ülke</label>
                  <select
                    value={newExpense.countryCode}
                    onChange={(e) => setNewExpense(prev => ({ ...prev, countryCode: e.target.value }))}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                    disabled={!!editingExpense}
                  >
                    <option value="">Seçin...</option>
                    {selectedMarketplace.countries.filter(c => c.isActive).map(country => (
                      <option key={country.code} value={country.code}>
                        {country.flag} {country.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Fulfillment</label>
                  <select
                    value={newExpense.fulfillmentType}
                    onChange={(e) => setNewExpense(prev => ({ ...prev, fulfillmentType: e.target.value as 'FBA' | 'FBM' | 'FBM-US' }))}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                    disabled={!!editingExpense}
                  >
                    <option value="FBA">FBA</option>
                    <option value="FBM">FBM</option>
                    <option value="FBM-TR">FBM-TR</option>
                    {newExpense.countryCode === 'US' && <option value="FBM-US">FBM-US (ABD Depo)</option>}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Kategori</label>
                  <select
                    value={newExpense.category}
                    onChange={(e) => setNewExpense(prev => ({ ...prev, category: e.target.value }))}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                    disabled={!!editingExpense}
                  >
                    <option value="">Seçin...</option>
                    <option value="__default__">Genel (Varsayılan)</option>
                    {categories.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Fee Inputs */}
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Selling Fee %</label>
                  <input
                    type="number"
                    value={newExpense.sellingFeePercent || ''}
                    onChange={(e) => setNewExpense(prev => ({ ...prev, sellingFeePercent: parseFloat(e.target.value) || 0 }))}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                    step="0.1"
                    placeholder="15.0"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">FBA Fee %</label>
                  <input
                    type="number"
                    value={newExpense.fbaFeePercent || ''}
                    onChange={(e) => setNewExpense(prev => ({ ...prev, fbaFeePercent: parseFloat(e.target.value) || 0 }))}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                    step="0.1"
                    placeholder="0.0"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Ads %</label>
                  <input
                    type="number"
                    value={newExpense.advertisingPercent || ''}
                    onChange={(e) => setNewExpense(prev => ({ ...prev, advertisingPercent: parseFloat(e.target.value) || 0 }))}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                    step="0.1"
                    placeholder="5.0"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Refund Loss %</label>
                  <input
                    type="number"
                    value={newExpense.refundLossPercent || ''}
                    onChange={(e) => setNewExpense(prev => ({ ...prev, refundLossPercent: parseFloat(e.target.value) || 0 }))}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                    step="0.1"
                    placeholder="2.0"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Marj %</label>
                  <input
                    type="number"
                    value={newExpense.marginPercent || ''}
                    onChange={(e) => setNewExpense(prev => ({ ...prev, marginPercent: parseFloat(e.target.value) || 0 }))}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                    step="0.1"
                    placeholder="20.0"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">ROI %</label>
                  <input
                    type="number"
                    value={newExpense.roiPercent || ''}
                    onChange={(e) => setNewExpense(prev => ({ ...prev, roiPercent: parseFloat(e.target.value) || 0 }))}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                    step="0.1"
                    placeholder="0.0"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">VAT %</label>
                  <input
                    type="number"
                    value={newExpense.vatPercent || ''}
                    onChange={(e) => setNewExpense(prev => ({ ...prev, vatPercent: parseFloat(e.target.value) || 0 }))}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                    step="0.1"
                    placeholder="0.0"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">GST %</label>
                  <input
                    type="number"
                    value={newExpense.gstPercent || ''}
                    onChange={(e) => setNewExpense(prev => ({ ...prev, gstPercent: parseFloat(e.target.value) || 0 }))}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                    step="0.1"
                    placeholder="0.0"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Customs %</label>
                  <input
                    type="number"
                    value={newExpense.customsPercent || ''}
                    onChange={(e) => setNewExpense(prev => ({ ...prev, customsPercent: parseFloat(e.target.value) || 0 }))}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                    step="0.1"
                    placeholder="0.0"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">DDP %</label>
                  <input
                    type="number"
                    value={newExpense.ddpPercent || ''}
                    onChange={(e) => setNewExpense(prev => ({ ...prev, ddpPercent: parseFloat(e.target.value) || 0 }))}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                    step="0.1"
                    placeholder="0.0"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Ship %</label>
                  <input
                    type="number"
                    value={newExpense.shipPercent || ''}
                    onChange={(e) => setNewExpense(prev => ({ ...prev, shipPercent: parseFloat(e.target.value) || 0 }))}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                    step="0.1"
                    placeholder="0.0"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Warehouse %</label>
                  <input
                    type="number"
                    value={newExpense.warehousePercent || ''}
                    onChange={(e) => setNewExpense(prev => ({ ...prev, warehousePercent: parseFloat(e.target.value) || 0 }))}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                    step="0.1"
                    placeholder="0.0"
                  />
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-2 pt-4 border-t border-slate-200">
                <button
                  onClick={() => {
                    setIsAddingExpense(false);
                    setEditingExpense(null);
                  }}
                  className="px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 rounded-lg"
                >
                  İptal
                </button>
                <button
                  onClick={handleSaveExpense}
                  className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg"
                >
                  Kaydet
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AmazonExpensesTab;
